import { faPlay } from "@fortawesome/pro-solid-svg-icons";
import { Tooltip, TooltipProps } from "@growtherapy/sprout-ui";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ControlPanelButton } from "../../components";
import { useGetTelehealthSessionInfo } from "../../hooks/useGetTelehealthSessionInfo";
import { useTrackEvent } from "../../segment/segment";
import { EventTypeEnum } from "../../segment/types";
import {
  CONSENT_TOAST_DURATION_MS,
  TRANSCRIPTION_DISABLED_TOAST_TIMEOUT,
} from "../../session/controls/constants";
import { RecordingControl as BaseRecordingControl } from "../../session/controls/RecordingControl";
import { RecordingControlMultipleParticipantsToast } from "../../session/controls/RecordingControlMultipleParticipantsToast";
import { useShouldDisableTranscriptionForMultipleParticipants } from "../../session/controls/useShouldDisableTranscriptionForMultipleParticipants";
import {
  patientConsentAtom,
  providerConsentAtom,
} from "../../session/messages/state";
import { toast } from "../../toasts";
import { UserType } from "../../types";
import {
  AudioRecordingState,
  ConsentStatus,
  isRecordingAtom,
  recordingStateAtom,
} from "../../video-sdk";
import { useSendVisitorParticipantStateCallback } from "../messages/useSendVisitorParticipantState";
import { ConsentDeclinedToast } from "./ConsentDeclinedToast";
import { ConsentModalWrapper as ConsentModal } from "./ConsentModal";
import { RecordingNoticeWrapper as RecordingNotice } from "./RecordingNotice";
import { useRecordingControl } from "./useRecordingControl";

export function RecordingControl() {
  const [showRecordingNotice, setShowRecordingNotice] = useState(false);
  const [hasShownRecordingNotice, setHasShownRecordingNotice] = useState(false);
  const [shouldShowConsentDeclinedToast, setShouldShouldConsentDeclinedToast] =
    useState(false);

  const [
    hasShownMultipleParticipantsNotice,
    setHasShownMultipleParticipantsNotice,
  ] = useState(false);
  const { userClicked } = useTrackEvent();
  const { providerShortId, patientShortId } = useParams();

  const { data } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const appointmentShortId = data?.telehealthSessionInfo?.appointment?.shortId;
  const { isVisible, recordingState } = useRecordingControl();
  const setIsRecording = useSetAtom(isRecordingAtom);
  const setRecordingState = useSetAtom(recordingStateAtom);
  const [patientConsentState, setPatientConsent] = useAtom(patientConsentAtom);
  const patientConsent = patientConsentState.value;
  const providerConsent = useAtomValue(providerConsentAtom).value;
  const isOpen = patientConsentState.value === ConsentStatus.DECIDING;

  const toggleRecording = (shouldRecord: boolean) => {
    setShowRecordingNotice(false);
    setIsRecording({ value: shouldRecord, lastUpdated: Date.now() });
    setRecordingState({
      value: shouldRecord
        ? AudioRecordingState.RECORDING
        : AudioRecordingState.NOT_RECORDING,
      lastUpdated: Date.now(),
    });
    sendParticipantState();
  };
  const sendParticipantState = useSendVisitorParticipantStateCallback();
  const bothPartiesConsented =
    patientConsent === ConsentStatus.OPTED_IN &&
    providerConsent === ConsentStatus.OPTED_IN;
  const isRecording = recordingState === AudioRecordingState.RECORDING;

  useEffect(
    function notifyPatientWhenRecordingFirstStarts() {
      if (isRecording && !hasShownRecordingNotice) {
        setShowRecordingNotice(true);
        setHasShownRecordingNotice(true);
      }
    },
    [isRecording, hasShownRecordingNotice],
  );

  /**
   * The below code is to prevent sessions with multiple participants from surfacing
   * Transcription features due to AI quality issues identified in these types of sessions.
   * It is behind a feature flag and can be removed when we improve AI features for these sessions.
   */

  const shouldDisableTranscriptionForMultipleParticipants =
    useShouldDisableTranscriptionForMultipleParticipants();

  const shouldShowMultipleParticipantTranscriptionDisabledToast =
    shouldDisableTranscriptionForMultipleParticipants &&
    isRecording &&
    !hasShownMultipleParticipantsNotice;

  useEffect(() => {
    if (shouldShowMultipleParticipantTranscriptionDisabledToast) {
      toast.enqueue(RecordingControlMultipleParticipantsToast, {
        timeout: TRANSCRIPTION_DISABLED_TOAST_TIMEOUT,
      });
      setHasShownMultipleParticipantsNotice(true);
    }

    if (shouldShowConsentDeclinedToast && !isOpen) {
      toast.enqueue(
        (ariaProps) => (
          <ConsentDeclinedToast {...ariaProps} visitorDeclinedConsent />
        ),
        { timeout: CONSENT_TOAST_DURATION_MS },
      );
      setShouldShouldConsentDeclinedToast(false);
    }
  }, [
    shouldShowMultipleParticipantTranscriptionDisabledToast,
    shouldShowConsentDeclinedToast,
    isOpen,
  ]);

  const tooltipProps = useMemo(
    () =>
      showRecordingNotice
        ? ({
            className: "!bg-transparent opacity-100",
            isOpen: true,
            clickable: true,
            text: (
              <RecordingNotice onClose={() => setShowRecordingNotice(false)} />
            ),
          } satisfies Partial<TooltipProps>)
        : undefined,
    [showRecordingNotice],
  );

  if (!isVisible) return null;

  return (
    <>
      <ConsentModal
        isOpen={isOpen}
        onStartRecording={() => setShowRecordingNotice(false)}
        setShouldShowClientConsentDeclinedToast={
          setShouldShouldConsentDeclinedToast
        }
        setPatientConsent={setPatientConsent}
      />
      {recordingState === AudioRecordingState.DISABLED ||
      shouldDisableTranscriptionForMultipleParticipants ? (
        <Tooltip
          text="Transcription is disabled"
          /**
           * Although this Tooltip wraps a ControlPanelButton, the tooltip only ever
           * shows when the ControlPanelButton is disabled. In order to make this
           * tooltip still accessible for all users, we wrap it in a <div/> so that
           * there is an element to add the tooltip props to and set
           * childIsInteractive={false} (since the ControlPanelButton won't be
           * actually interactive when the tooltip is enabled).
           */
          childIsInteractive={false}
        >
          <div>
            <ControlPanelButton
              disabled={true}
              aria-label="Transcription is disabled"
              iconDefinition={faPlay}
              data-testid="recording-control"
            />
          </div>
        </Tooltip>
      ) : (
        (bothPartiesConsented || isRecording) && (
          <BaseRecordingControl
            className="bg-lilac-700 hover:bg-lilac-600"
            isRecording={isRecording}
            onRecordingToggle={(shouldRecord: boolean) => {
              toggleRecording(shouldRecord);
              userClicked(EventTypeEnum.CLICK_RECORDING_TOGGLE, {
                appointmentShortId: appointmentShortId!,
                newRecordingStatus: shouldRecord ? "PLAY" : "PAUSE",
                entityType: UserType.CLIENT,
              });
            }}
            consentPending={false} // Clients don't see this until they've consented
            tooltipProps={tooltipProps}
          />
        )
      )}
    </>
  );
}
